// SPDX-License-Identifier: AGPL-3.0-or-later

import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import type {
	IARPrimaryPath,
	IARReportContextType,
	IARRuleReasonId,
} from '@app/features/devtools/components/modals/IARFlowUtils';
import type {
	IARActionCardConfig,
	IARActionHandlers,
	IARContext,
	IARCopyBlock,
	IARGuidanceContent,
	IARProgressState,
	IARRadioOption,
	IARResolvedContext,
	IARStep,
} from '@app/features/devtools/components/modals/IARModal.types';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

function shouldShowContactPath(context: IARContext): boolean {
	return context.type !== 'guild';
}

function shouldShowContactSettings(context: IARContext): boolean {
	return context.type !== 'guild';
}

function getPreferenceOptionName(i18n: I18n, context: IARContext): string {
	switch (context.type) {
		case 'message':
			return i18n._(msg`I just don't like it`);
		case 'user':
			return i18n._(msg`I just don't like this user`);
		case 'guild':
			return i18n._(msg`I just don't like this community`);
	}
}

function getPreferenceOptionDescription(i18n: I18n, context: IARContext): string {
	switch (context.type) {
		case 'message':
		case 'user':
			return i18n._(msg`Not a rule violation on its own. Try block, mute, or leave instead.`);
		case 'guild':
			return i18n._(msg`Not a rule violation on its own. Leave the community instead.`);
	}
}

function getPreferenceGuidanceTitle(i18n: I18n, context: IARContext): string {
	switch (context.type) {
		case 'message':
			return i18n._(msg`Disliking a message isn't a rule violation`);
		case 'user':
			return i18n._(msg`Disliking a user isn't a rule violation`);
		case 'guild':
			return i18n._(msg`Disliking a community isn't a rule violation`);
	}
}

function getPreferenceGuidanceDescription(i18n: I18n, context: IARContext, subjectLabel: string): string {
	switch (context.type) {
		case 'message':
		case 'user':
			return i18n._(msg`Don't report just because you dislike this ${subjectLabel}. Use the tools below for distance.`);
		case 'guild':
			return i18n._(msg`Don't report just because you dislike this community. Leave it instead.`);
	}
}

export function getIARModalDescription(i18n: I18n): string {
	return i18n._(msg`Report rule violations or find tools to manage contact, moderation, and preferences.`);
}

export function getIARPathPanelCopy(i18n: I18n): IARCopyBlock {
	return {
		title: i18n._(msg`What do you need?`),
		body: i18n._(msg`Pick the option that best matches your situation.`),
	};
}

export function getIARReportEligibilityCopy(i18n: I18n): IARCopyBlock {
	return {
		title: i18n._(msg`Finish account setup first`),
		body: i18n._(msg`Claim your account and verify your email to send reports. Other tools remain available.`),
	};
}

export function getIARReasonPanelCopy(i18n: I18n): IARCopyBlock {
	return {
		title: i18n._(msg`Which rule was broken?`),
		body: i18n._(msg`Pick the closest match. The Safety Team will review it.`),
	};
}

function getChildSafetyReasonName(i18n: I18n, contextType: IARReportContextType): string {
	switch (contextType) {
		case 'message':
		case 'guild':
			return i18n._(msg`Child safety or sexual exploitation of minors`);
		case 'user':
			return i18n._(msg`Underage or child-safety concern`);
	}
}

export function getIARChildSafetyRoutingNote(
	i18n: I18n,
	contextType: IARReportContextType,
	selectedReason: IARRuleReasonId | null,
): IARCopyBlock | null {
	if (selectedReason === null) {
		return null;
	}

	const childSafetyReasonName = getChildSafetyReasonName(i18n, contextType);

	switch (selectedReason) {
		case 'illegal_activity':
			return {
				title: i18n._(msg`Minors involved? Use the child-safety rule`),
				body: i18n._(
					msg`Do not use "Illegal activity" for CSAM, grooming, or sexual exploitation of minors. Go back and select "${childSafetyReasonName}" so Safety Team receives the report in the correct child-safety queue.`,
				),
			};
		case 'sexual_content':
			return {
				title: i18n._(msg`Involves a minor? Use the child-safety rule`),
				body: i18n._(
					msg`Do not use "Sexual content" for CSAM, grooming, or sexual exploitation of minors. Go back and select "${childSafetyReasonName}" instead.`,
				),
			};
		case 'other':
			return {
				title: i18n._(msg`Do not use "Other" for child-safety reports`),
				body: i18n._(
					msg`If this involves a minor, CSAM, grooming, or sexual exploitation of minors, go back and select "${childSafetyReasonName}" instead of "Other".`,
				),
			};
		case 'child_safety':
		case 'harassment':
		case 'hate':
		case 'violence':
		case 'harmful_misinformation':
		case 'spam_scams':
		case 'malware':
		case 'privacy':
		case 'impersonation':
		case 'self_harm':
		case 'terrorism_extremism':
		case 'inappropriate_profile':
		case 'raid_coordination':
			return null;
	}
}

export function getIARReviewPanelCopy(
	i18n: I18n,
	selectedReasonOption: IARRadioOption<IARRuleReasonId> | null,
): IARCopyBlock {
	if (selectedReasonOption !== null) {
		return {
			title: selectedReasonOption.name,
			body: selectedReasonOption.desc ?? '',
		};
	}

	return {
		title: i18n._(msg`Review your report`),
		body: i18n._(msg`The Safety Team will review this against ${PRODUCT_NAME}'s rules.`),
	};
}

export function getIARReviewHandlingCopy(i18n: I18n, resolvedContext: IARResolvedContext): IARCopyBlock {
	const tips: Array<string> = [];

	if (resolvedContext.hasCommunityContext) {
		tips.push(i18n._(msg`For local action, notify that community's moderators too.`));
	}

	if (resolvedContext.reportedUser !== null) {
		tips.push(i18n._(msg`To stop contact, block them or adjust privacy settings.`));
	}

	if (tips.length === 0) {
		return {
			title: i18n._(msg`Before you submit`),
			body: i18n._(msg`The Safety Team reviews every report. Submit only in good faith.`),
		};
	}

	return {
		title: i18n._(msg`Before you submit`),
		body: tips.join(' '),
	};
}

export function getIARReviewContextCopy(i18n: I18n): IARCopyBlock {
	return {
		title: i18n._(msg`Add context (optional)`),
		body: i18n._(msg`Facts and context help the Safety Team. Don't submit false or bad-faith reports.`),
	};
}

export function getIARGuidanceContent(
	i18n: I18n,
	selectedPath: Exclude<IARPrimaryPath, 'platform'>,
	context: IARContext,
	resolvedContext: IARResolvedContext,
): IARGuidanceContent {
	switch (selectedPath) {
		case 'community':
			return {
				title: i18n._(msg`Community moderators can help`),
				description: i18n._(msg`Local rules, off-topic posts, or spoilers are best handled by that community's mods.`),
				noteTitle: i18n._(msg`Heads up`),
				noteBody: i18n._(
					msg`This doesn't send a report to ${PRODUCT_NAME}. If it also breaks our rules, go back and file a report too.`,
				),
				switchButtonLabel: i18n._(msg`Report a rule violation instead`),
			};
		case 'contact':
			return {
				title: i18n._(msg`Take control of your contact`),
				description: i18n._(msg`Block, close the DM, or adjust who can reach you — in a few clicks.`),
				noteTitle: i18n._(msg`Heads up`),
				noteBody: i18n._(
					msg`This doesn't send a report to ${PRODUCT_NAME}. If the conduct also breaks our rules, go back and file a report too.`,
				),
				switchButtonLabel: i18n._(msg`Report a rule violation instead`),
			};
		case 'preference':
			return {
				title: getPreferenceGuidanceTitle(i18n, context),
				description: getPreferenceGuidanceDescription(i18n, context, resolvedContext.subjectLabel),
				noteTitle: i18n._(msg`When to go back`),
				noteBody: i18n._(msg`Only go back if this also breaks ${PRODUCT_NAME}'s Terms or Community Guidelines.`),
				switchButtonLabel: i18n._(msg`Report a rule violation instead`),
			};
	}
}

export function getIARProgressState(i18n: I18n, step: IARStep, selectedPath: IARPrimaryPath | null): IARProgressState {
	let steps: Array<string>;

	if (step === 'reason' || step === 'review' || selectedPath === 'platform') {
		steps = [i18n._(msg`Route`), i18n._(msg`Rule`), i18n._(msg`Review`)];
	} else {
		steps = [i18n._(msg`Route`), i18n._(msg`Next`)];
	}

	switch (step) {
		case 'path':
			return {steps, currentIndex: 0};
		case 'reason':
			return {steps, currentIndex: 1};
		case 'review':
		case 'guidance':
			return {steps, currentIndex: steps.length - 1};
	}
}

export function getIARPrimaryOptions(
	i18n: I18n,
	context: IARContext,
	resolvedContext: IARResolvedContext,
): Array<IARRadioOption<IARPrimaryPath>> {
	const options: Array<IARRadioOption<IARPrimaryPath>> = [
		{
			value: 'platform',
			name: i18n._(msg`Report a rule violation`),
			desc: i18n._(msg`Harassment, hate, threats, sexual exploitation, scams, privacy abuse, and more.`),
		},
	];

	if (resolvedContext.hasCommunityContext) {
		options.push({
			value: 'community',
			name: i18n._(msg`Community moderation issue`),
			desc: i18n._(msg`Local rules, off-topic posts, spoilers — handled by that community's mods.`),
		});
	}

	if (shouldShowContactPath(context)) {
		options.push({
			value: 'contact',
			name: i18n._(msg`Stop or reduce contact`),
			desc: i18n._(msg`Block, close DM, or change who can message, friend, or call you.`),
		});
	}

	options.push({
		value: 'preference',
		name: getPreferenceOptionName(i18n, context),
		desc: getPreferenceOptionDescription(i18n, context),
	});

	return options;
}

function getMessageRuleReasonOptions(i18n: I18n): Array<IARRadioOption<IARRuleReasonId>> {
	return [
		{
			value: 'harassment',
			name: i18n._(msg`Harassment or threats`),
			desc: i18n._(msg`Bullying, dogpiling, repeated unwanted contact, stalking, or targeted abuse.`),
		},
		{
			value: 'hate',
			name: i18n._(msg`Hate speech`),
			desc: i18n._(msg`Slurs, dehumanizing language, or attacks on protected characteristics.`),
		},
		{
			value: 'violence',
			name: i18n._(msg`Violence or violent threats`),
			desc: i18n._(msg`Credible threats, graphic violence, or glorification of violence.`),
		},
		{
			value: 'sexual_content',
			name: i18n._(msg`Sexual content or harassment`),
			desc: i18n._(
				msg`Unwanted sexual conduct, explicit content in the wrong place, or intimate content shared without consent. If a minor is involved, use "Child safety or sexual exploitation of minors" instead.`,
			),
		},
		{
			value: 'child_safety',
			name: i18n._(msg`Child safety or sexual exploitation of minors`),
			desc: i18n._(msg`Grooming, sexualization of minors, CSAM, or child-exploitation content.`),
		},
		{
			value: 'harmful_misinformation',
			name: i18n._(msg`Harmful misinformation`),
			desc: i18n._(msg`False claims likely to cause real-world harm, like dangerous medical lies.`),
		},
		{
			value: 'spam_scams',
			name: i18n._(msg`Spam, scams, or phishing`),
			desc: i18n._(msg`Mass spam, fraud, fake giveaways, or account abuse.`),
		},
		{
			value: 'malware',
			name: i18n._(msg`Malware or dangerous links`),
			desc: i18n._(msg`Malware, credential theft, or harmful files.`),
		},
		{
			value: 'privacy',
			name: i18n._(msg`Privacy violation`),
			desc: i18n._(msg`Doxxing, exposed private info, or stalking.`),
		},
		{
			value: 'impersonation',
			name: i18n._(msg`Impersonation or deceptive media`),
			desc: i18n._(msg`Pretending to be someone else, including deceptive AI-generated content.`),
		},
		{
			value: 'illegal_activity',
			name: i18n._(msg`Illegal activity`),
			desc: i18n._(
				msg`Illegal sales, criminal facilitation, or unlawful activity. If minors, CSAM, grooming, or sexual exploitation of minors are involved, use "Child safety or sexual exploitation of minors" instead.`,
			),
		},
		{
			value: 'self_harm',
			name: i18n._(msg`Self-Harm or Suicide`),
			desc: i18n._(msg`Promotion, glorification, or instructions encouraging self-harm or eating disorders.`),
		},
		{
			value: 'other',
			name: i18n._(msg`Another clear rule violation`),
			desc: i18n._(msg`Use only if it clearly breaks ${PRODUCT_NAME}'s rules and doesn't fit above.`),
		},
	];
}

function getUserRuleReasonOptions(i18n: I18n): Array<IARRadioOption<IARRuleReasonId>> {
	return [
		{
			value: 'harassment',
			name: i18n._(msg`Harassment or stalking`),
			desc: i18n._(msg`Threats, following across spaces, or unwanted contact.`),
		},
		{
			value: 'hate',
			name: i18n._(msg`Hate speech`),
			desc: i18n._(msg`Targets people by protected characteristics.`),
		},
		{
			value: 'sexual_content',
			name: i18n._(msg`Sexual harassment`),
			desc: i18n._(
				msg`Unwanted sexual comments, requests, or conduct. If a minor is involved, use "Underage or child-safety concern" instead.`,
			),
		},
		{
			value: 'child_safety',
			name: i18n._(msg`Underage or child-safety concern`),
			desc: i18n._(msg`User appears underage, or is involved in grooming or child exploitation.`),
		},
		{
			value: 'harmful_misinformation',
			name: i18n._(msg`Harmful misinformation`),
			desc: i18n._(msg`Repeatedly spreads false claims likely to cause serious harm.`),
		},
		{
			value: 'spam_scams',
			name: i18n._(msg`Spam, scam, or phishing account`),
			desc: i18n._(msg`Account exists to spam, scam, or abuse the platform.`),
		},
		{
			value: 'malware',
			name: i18n._(msg`Sending malware or dangerous links`),
			desc: i18n._(msg`Credential theft or harmful files.`),
		},
		{
			value: 'privacy',
			name: i18n._(msg`Privacy violation or stalking`),
			desc: i18n._(msg`Doxxing, stalking, or invading others' privacy.`),
		},
		{
			value: 'impersonation',
			name: i18n._(msg`Impersonation`),
			desc: i18n._(msg`Claiming to be another person, brand, or organization.`),
		},
		{
			value: 'inappropriate_profile',
			name: i18n._(msg`Profile, username, or avatar violation`),
			desc: i18n._(msg`Violation is in the user's profile, not a specific message.`),
		},
		{
			value: 'illegal_activity',
			name: i18n._(msg`Illegal activity`),
			desc: i18n._(
				msg`Promotes or facilitates unlawful activity. If minors, CSAM, grooming, or sexual exploitation of minors are involved, use "Underage or child-safety concern" instead.`,
			),
		},
		{
			value: 'self_harm',
			name: i18n._(msg`Encourages self-harm`),
			desc: i18n._(msg`Encourages suicide, self-harm, or eating disorders.`),
		},
		{
			value: 'other',
			name: i18n._(msg`Another clear rule violation`),
			desc: i18n._(msg`Use only if it clearly breaks ${PRODUCT_NAME}'s rules and doesn't fit above.`),
		},
	];
}

function getGuildRuleReasonOptions(i18n: I18n): Array<IARRadioOption<IARRuleReasonId>> {
	return [
		{
			value: 'harassment',
			name: i18n._(msg`Harassment or targeted abuse`),
			desc: i18n._(msg`Community facilitates pile-ons or targeted abuse.`),
		},
		{
			value: 'hate',
			name: i18n._(msg`Hate speech`),
			desc: i18n._(msg`Promotes hatred against protected groups.`),
		},
		{
			value: 'terrorism_extremism',
			name: i18n._(msg`Terrorism or violent extremism`),
			desc: i18n._(msg`Promotes, recruits for, or coordinates violent extremist activity.`),
		},
		{
			value: 'sexual_content',
			name: i18n._(msg`Sexual content or unsafe age-gating`),
			desc: i18n._(
				msg`Sexual content without proper age-gating. If minors are involved, use "Child safety or sexual exploitation of minors" instead.`,
			),
		},
		{
			value: 'child_safety',
			name: i18n._(msg`Child safety or sexual exploitation of minors`),
			desc: i18n._(msg`Endangers minors or hosts child-exploitation content.`),
		},
		{
			value: 'harmful_misinformation',
			name: i18n._(msg`Harmful misinformation`),
			desc: i18n._(msg`Organizes or spreads false claims likely to cause serious harm.`),
		},
		{
			value: 'raid_coordination',
			name: i18n._(msg`Raid Coordination`),
			desc: i18n._(msg`Coordinates raids, brigading, or harassment against people or communities.`),
		},
		{
			value: 'spam_scams',
			name: i18n._(msg`Spam, scams, or phishing`),
			desc: i18n._(msg`Community exists to spam, scam, or abuse the platform.`),
		},
		{
			value: 'malware',
			name: i18n._(msg`Malware distribution`),
			desc: i18n._(msg`Distributes malware, credential theft, or harmful files.`),
		},
		{
			value: 'privacy',
			name: i18n._(msg`Privacy violation or doxxing`),
			desc: i18n._(msg`Shares personal info, stalks users, or coordinates privacy abuse.`),
		},
		{
			value: 'illegal_activity',
			name: i18n._(msg`Illegal activity`),
			desc: i18n._(
				msg`Promotes or facilitates unlawful activity. If minors, CSAM, grooming, or sexual exploitation of minors are involved, use "Child safety or sexual exploitation of minors" instead.`,
			),
		},
		{
			value: 'self_harm',
			name: i18n._(msg`Encourages self-harm`),
			desc: i18n._(msg`Encourages suicide, self-harm, or eating disorders.`),
		},
		{
			value: 'other',
			name: i18n._(msg`Another clear rule violation`),
			desc: i18n._(msg`Use only if it clearly breaks ${PRODUCT_NAME}'s rules and doesn't fit above.`),
		},
	];
}

export function getIARRuleReasonOptions(
	i18n: I18n,
	contextType: IARReportContextType,
): Array<IARRadioOption<IARRuleReasonId>> {
	switch (contextType) {
		case 'message':
			return getMessageRuleReasonOptions(i18n);
		case 'user':
			return getUserRuleReasonOptions(i18n);
		case 'guild':
			return getGuildRuleReasonOptions(i18n);
	}
}

export function getIARSpecialSafetyNote(
	i18n: I18n,
	step: IARStep,
	selectedReason: IARRuleReasonId | null,
): string | null {
	if (step !== 'review' || selectedReason === null) {
		return null;
	}

	switch (selectedReason) {
		case 'child_safety':
			return i18n._(
				msg`If this involves CSAM or sexual exploitation of a minor, submit now and don't reshare the material.`,
			);
		case 'self_harm':
			return i18n._(
				msg`If someone may be in immediate danger, contact local emergency services if you can do so safely.`,
			);
		case 'violence':
			return i18n._(msg`If this is a credible imminent threat, contact local emergency services too.`);
		case 'terrorism_extremism':
			return i18n._(msg`If this is an imminent terrorist threat, contact local emergency services too.`);
		case 'privacy':
			return i18n._(msg`Don't paste private personal info again unless necessary for review.`);
		case 'harmful_misinformation':
			return i18n._(msg`Explain the claim, why it's false, and the serious harm it could cause.`);
		case 'raid_coordination':
			return i18n._(msg`If there's active coordination right now, notify the affected moderators too.`);
		case 'harassment':
		case 'hate':
		case 'sexual_content':
		case 'illegal_activity':
		case 'spam_scams':
		case 'malware':
		case 'impersonation':
		case 'inappropriate_profile':
		case 'other':
			return null;
	}
}

export function getIARActionCards(
	i18n: I18n,
	context: IARContext,
	resolvedContext: IARResolvedContext,
	handlers: IARActionHandlers,
): Array<IARActionCardConfig> {
	const cards: Array<IARActionCardConfig> = [];

	if (!resolvedContext.isReportedUserBlocked && resolvedContext.reportedUser !== null) {
		cards.push({
			id: 'block-user',
			title: i18n._(msg`Block this user`),
			description: i18n._(msg`Stop messages and friend requests.`),
			label: i18n._(msg`Block`),
			buttonVariant: 'danger-secondary',
			onClick: handlers.onBlockUser,
		});
	}

	if (context.type === 'message' && resolvedContext.hasCommunityContext) {
		cards.push({
			id: 'copy-message-link',
			title: i18n._(msg`Copy Message Link`),
			description: i18n._(msg`Share with community mods.`),
			label: i18n._(msg`Copy`),
			buttonVariant: 'secondary',
			onClick: handlers.onCopyMessageLink,
		});
	}

	if (resolvedContext.dmChannel !== null) {
		cards.push({
			id: 'close-dm',
			title: i18n._(msg`Close this DM`),
			description: i18n._(msg`Doesn't block. You can reopen later.`),
			label: i18n._(msg`Close DM`),
			buttonVariant: 'danger-secondary',
			onClick: handlers.onCloseDM,
		});
	}

	if (resolvedContext.leaveableGuildId !== null) {
		cards.push({
			id: 'leave-community',
			title: i18n._(msg`Leave the community`),
			description: i18n._(msg`Stop seeing its content and members.`),
			label: i18n._(msg`Leave`),
			buttonVariant: 'danger-secondary',
			onClick: handlers.onLeaveCommunity,
		});
	}

	if (shouldShowContactSettings(context)) {
		cards.push({
			id: 'connections-settings',
			title: i18n._(msg`DM & friend request settings`),
			description: i18n._(msg`Change who can reach you.`),
			label: i18n._(msg`Open`),
			buttonVariant: 'secondary',
			onClick: handlers.onOpenConnectionsSettings,
		});
		cards.push({
			id: 'communication-settings',
			title: i18n._(msg`Call & group chat settings`),
			description: i18n._(msg`Change who can call or add you.`),
			label: i18n._(msg`Open`),
			buttonVariant: 'secondary',
			onClick: handlers.onOpenCommunicationSettings,
		});
	}

	return cards;
}
